import React from "react"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Modal from "react-bootstrap/Modal"
import Spinner from "react-bootstrap/Spinner"
import { Search } from "lucide-react"
import Badge from "../common/Badge.jsx"
import Button from "../common/Button.jsx"
import TableWithPagination from "../table/TableWithPagination.jsx"
import { refetchRemainingMs } from "../utils/loading.js"

const MANIFESTS_PER_PAGE = 5
const MIN_ACTION_SPINNER_MS = 300

function getStatusBadgeVariant(status) {
    switch ((status ?? "").toLowerCase()) {
    case "received":
        return "info"
    case "shipped":
        return "warning"
    case "open":
        return "success"
    default:
        return "default"
    }
}

function renderTextCell(value, className = "", style = {}, cellProps = {}) {
    const displayValue = value ?? ""
    return (
        <td
            style={{ ...(cellProps.style ?? {}), ...style }}
            className={`${className} ${cellProps.className ?? ""}`.trim()}
        >
            <span
                title={displayValue}
                style={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {displayValue}
            </span>
        </td>
    )
}

/**
 * @param {{
 *   show: boolean,
 *   onClose: () => void,
 *   order: any,
 *   locations: any[],
 *   api: any,
 *   helpers?: { formatStatus?: (value: any) => string },
 *   onUpdated: () => Promise<void> | void,
 *   onError: (error: unknown) => void,
 *   onSuccess: (message: string) => void,
 * }} props
 */
export default function EditManifestModal({
    show,
    onClose,
    order,
    locations,
    api,
    helpers,
    onUpdated,
    onError,
    onSuccess,
}) {
    const [manifests, setManifests] = React.useState([])
    const [totalRows, setTotalRows] = React.useState(0)
    const [page, setPage] = React.useState(1)
    const [searchInput, setSearchInput] = React.useState("")
    const [searchQuery, setSearchQuery] = React.useState("")
    const [allLocations, setAllLocations] = React.useState(false)
    const [selectedManifestId, setSelectedManifestId] = React.useState(
        /** @type {number | null} */ (null)
    )
    const [isLoading, setIsLoading] = React.useState(false)
    const [modalTableBootstrapped, setModalTableBootstrapped] = React.useState(false)
    const fetchRequestIdRef = React.useRef(0)
    const [pendingAction, setPendingAction] = React.useState(
        /** @type {"assign" | "remove" | null} */ (null)
    )

    const selectedManifest = manifests.find((x) => x.id === selectedManifestId) ?? null

    async function ensureMinimumSpinnerTime(startedAtMs) {
        const elapsedMs = Date.now() - startedAtMs
        const remainingMs = MIN_ACTION_SPINNER_MS - elapsedMs
        if (remainingMs > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remainingMs))
        }
    }

    const headers = React.useMemo(() => {
        const HEADER_CLASS = "text-uppercase text-secondary"
        const HEADER_STYLE = { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.2 }
        return [
            {
                key: "location",
                label: "Location",
                className: HEADER_CLASS,
                style: HEADER_STYLE,
                minWidth: 80,
                preferredWidth: 120,
            },
            {
                key: "manifestNum",
                label: "Manifest #",
                className: HEADER_CLASS,
                style: HEADER_STYLE,
                minWidth: 80,
                preferredWidth: 120,
            },
            {
                key: "numOrders",
                label: "# Orders",
                className: HEADER_CLASS,
                style: HEADER_STYLE,
                minWidth: 70,
                preferredWidth: 90,
            },
            {
                key: "status",
                label: "Status",
                className: HEADER_CLASS,
                style: HEADER_STYLE,
                minWidth: 100,
                preferredWidth: 120,
            },
        ]
    }, [])

    function mapManifest(manifest) {
        const formatStatus = helpers?.formatStatus ?? ((status) => status)
        const location = locations.find((x) => x.id === manifest.location_id)
        return {
            id: manifest.id,
            location: location?.name ?? "",
            manifestNum: manifest.manifest_num ?? "",
            numOrders: manifest.num_orders ?? 0,
            status: formatStatus(manifest.status ?? ""),
        }
    }

    function getLocationQuery() {
        if (allLocations || searchQuery.trim()) {
            return ""
        }
        const store = locations.find((x) => x.id === order?.location_id)
        return store ? store.id : ""
    }

    function fetchManifests() {
        if (!show || !order?.id) {
            return
        }
        const requestId = ++fetchRequestIdRef.current
        const isRefetch = modalTableBootstrapped
        const fetchStartedAt = Date.now()
        setIsLoading(true)
        api.getManifests(
            page,
            MANIFESTS_PER_PAGE,
            null,
            null,
            searchQuery.trim(),
            getLocationQuery()
        )
            .then((body) => {
                if (requestId !== fetchRequestIdRef.current) {
                    return
                }
                const mapped = (body?.data ?? []).map(mapManifest)
                const total = body?.total ?? 0
                if (!isRefetch) {
                    setManifests(mapped)
                    setTotalRows(total)
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== fetchRequestIdRef.current) {
                        return
                    }
                    setManifests(mapped)
                    setTotalRows(total)
                    setIsLoading(false)
                }, delay)
            })
            .catch((error) => {
                if (requestId !== fetchRequestIdRef.current) {
                    return
                }
                onError(error)
                if (!isRefetch) {
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== fetchRequestIdRef.current) {
                        return
                    }
                    setIsLoading(false)
                }, delay)
            })
            .finally(() => {
                if (requestId !== fetchRequestIdRef.current) {
                    return
                }
                if (isRefetch) {
                    return
                }
                setModalTableBootstrapped(true)
                setIsLoading(false)
            })
    }

    React.useEffect(() => {
        if (!show) {
            return
        }
        fetchManifests()
    }, [show, page, searchQuery, allLocations, order?.id])

    React.useEffect(() => {
        if (!show) {
            setPage(1)
            setSearchInput("")
            setSearchQuery("")
            setAllLocations(false)
            setSelectedManifestId(null)
            setModalTableBootstrapped(false)
        }
    }, [show])

    function renderManifestRow(manifest, index, tableTools) {
        const getCellProps = tableTools?.getCellProps ?? (() => ({}))
        const getRowProps = tableTools?.getRowProps ?? (() => ({}))
        const isSelected = selectedManifestId === manifest.id
        const selectedCellStyle = isSelected ?
            { backgroundColor: "rgba(15, 97, 112, 0.12)" } :
            {}
        return (
            <tr key={manifest.id} {...getRowProps(manifest, index)}>
                {renderTextCell(
                    manifest.location,
                    "text-dark",
                    { fontSize: "0.875rem", ...selectedCellStyle },
                    getCellProps("location")
                )}
                {renderTextCell(
                    manifest.manifestNum,
                    "fw-semibold",
                    { fontSize: "0.875rem", color: "#0f6170", ...selectedCellStyle },
                    getCellProps("manifestNum")
                )}
                {renderTextCell(
                    String(manifest.numOrders),
                    "text-dark",
                    { fontSize: "0.875rem", ...selectedCellStyle },
                    getCellProps("numOrders")
                )}
                <td
                    {...getCellProps("status")}
                    style={{ width: "1%", whiteSpace: "nowrap", ...selectedCellStyle }}
                >
                    <Badge variant={getStatusBadgeVariant(manifest.status)}>
                        {manifest.status}
                    </Badge>
                </td>
            </tr>
        )
    }

    function handleSearchSubmit(event) {
        event.preventDefault()
        setSelectedManifestId(null)
        setPage(1)
        setSearchQuery(searchInput)
    }

    async function handleRemoveManifest() {
        if (!order?.id || pendingAction != null) {
            return
        }
        const startedAtMs = Date.now()
        setPendingAction("remove")
        try {
            await api.clearOrderManifest(order.id)
            await ensureMinimumSpinnerTime(startedAtMs)
            await onUpdated()
            onSuccess("Manifest removed successfully.")
            onClose()
        } catch (error) {
            await ensureMinimumSpinnerTime(startedAtMs)
            onError(error)
        } finally {
            setPendingAction(null)
        }
    }

    async function handleAssignManifest() {
        if (!order?.id || !selectedManifest || pendingAction != null) {
            return
        }
        const startedAtMs = Date.now()
        setPendingAction("assign")
        try {
            await api.assignOrderManifest(order.id, selectedManifest.id)
            await ensureMinimumSpinnerTime(startedAtMs)
            await onUpdated()
            onSuccess("Manifest assigned successfully.")
            onClose()
        } catch (error) {
            await ensureMinimumSpinnerTime(startedAtMs)
            onError(error)
        } finally {
            setPendingAction(null)
        }
    }

    return (
        <Modal size="lg" show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit Manifest for Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mt-2 mb-3">
                    <form onSubmit={handleSearchSubmit}>
                        <InputGroup>
                            <InputGroup.Text id="manifest-search-icon">
                                <Search size={16} aria-hidden />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Search manifests..."
                                aria-label="Search manifests"
                                value={searchInput}
                                onChange={(event) => setSearchInput(event.currentTarget.value)}
                            />
                        </InputGroup>
                    </form>
                </div>

                <div className="mb-3">
                    <button
                        type="button"
                        className={`btn btn-sm ${
                            allLocations ? "btn-dark" : "btn-outline-secondary"
                        }`}
                        onClick={() => {
                            setSelectedManifestId(null)
                            setPage(1)
                            setAllLocations((prev) => !prev)
                        }}
                    >
                        All locations
                    </button>
                </div>

                {isLoading && !modalTableBootstrapped ? (
                    <div className="bg-white rounded p-3 text-secondary">
                        Loading manifests...
                    </div>
                ) : (
                    <TableWithPagination
                        headers={headers}
                        rows={manifests}
                        renderRow={renderManifestRow}
                        totalRows={totalRows}
                        page={page}
                        perPage={MANIFESTS_PER_PAGE}
                        perPageSelection={MANIFESTS_PER_PAGE}
                        onPageChange={setPage}
                        onPerPageChange={() => {}}
                        rowsPerPageOptions={[MANIFESTS_PER_PAGE]}
                        showFitOption={false}
                        showOnMobile={false}
                        selectedRowKey={selectedManifestId}
                        getRowKey={(row) => row.id}
                        onRowSelect={(row) => setSelectedManifestId(row.id)}
                        showRefetchLoader={isLoading && modalTableBootstrapped}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline" onClick={onClose} disabled={pendingAction != null}>
                    Cancel
                </Button>
                <Button
                    variant="outline"
                    onClick={handleRemoveManifest}
                    disabled={pendingAction != null}
                >
                    {pendingAction === "remove" ? (
                        <span className="d-inline-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Remove from Manifest
                        </span>
                    ) : (
                        "Remove from Manifest"
                    )}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleAssignManifest}
                    disabled={pendingAction != null || !selectedManifest}
                >
                    {pendingAction === "assign" ? (
                        <span className="d-inline-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Assign to Manifest
                        </span>
                    ) : (
                        "Assign to Manifest"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
